import { Box, Typography, useTheme } from "@mui/material";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import React, { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ActivityTrend } from "../../../store/services/salesManagerService";

interface ActivityTrendsChartProps {
  trends: ActivityTrend[];
  height?: number;
}

/**
 * Linjediagram som visar schemalagda vs slutförda aktiviteter per dag.
 * Används både på översiktsfliken (komprimerad höjd) och aktivitetsfliken
 * (större höjd).
 */
const ActivityTrendsChart: React.FC<ActivityTrendsChartProps> = ({
  trends,
  height = 260,
}) => {
  const theme = useTheme();

  const data = useMemo(
    () =>
      trends.map((t) => ({
        ...t,
        label: format(parseISO(t.Date), "d MMM", { locale: sv }),
      })),
    [trends]
  );

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Ingen trenddata tillgänglig
        </Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={theme.palette.divider}
          vertical={false}
        />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          tickLine={false}
          axisLine={{ stroke: theme.palette.divider }}
        />
        <YAxis
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          tickLine={false}
          axisLine={{ stroke: theme.palette.divider }}
          allowDecimals={false}
        />
        <Tooltip
          contentStyle={{
            borderRadius: 8,
            border: `1px solid ${theme.palette.divider}`,
            fontSize: 12,
          }}
          labelStyle={{ fontWeight: 600 }}
        />
        <Legend
          wrapperStyle={{ fontSize: 12 }}
          iconType="circle"
          verticalAlign="top"
          height={28}
        />
        <Line
          type="monotone"
          dataKey="Scheduled"
          name="Schemalagda"
          stroke={theme.palette.info.main}
          strokeWidth={2}
          dot={{ r: 3 }}
          activeDot={{ r: 5 }}
        />
        <Line
          type="monotone"
          dataKey="Completed"
          name="Slutförda"
          stroke={theme.palette.success.main}
          strokeWidth={2}
          dot={{ r: 3 }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default ActivityTrendsChart;
